import { apiUrl } from 'shared/apiUrl';
import { LOCALE_LANGUAGES } from 'shared/types/locale';
import { frontendVersionWithBuildNumber } from 'shared/version';

/** What the worker needs to call the API for the user (the token is pushed separately). */
export interface ServiceWorkerConfigInput {
  frontendVersion: string;
  api: string;
  /** Language of the API answers, e.g. `de-DE`. */
  locale: string;
  projectId?: string;
  organizationId?: string;
  /** Project timezone, for date calculations in the worker (analytics). */
  timezone?: string;
}

/** The page's selection the worker is configured for. */
export interface ServiceWorkerSelection {
  /** Language of the app (ISO 639-1), e.g. `de`. */
  locale: string;
  projectId?: string;
  organizationId?: string;
  timezone?: string;
}

/** The worker's config for the selection; `undefined` while the app cannot configure it yet. */
export function toServiceWorkerConfigInput({
  locale,
  projectId,
  organizationId,
  timezone,
}: ServiceWorkerSelection): ServiceWorkerConfigInput | undefined {
  const language = LOCALE_LANGUAGES[locale];
  if (!frontendVersionWithBuildNumber || !apiUrl || !language) {
    return undefined;
  }
  return {
    frontendVersion: frontendVersionWithBuildNumber,
    api: apiUrl,
    locale: language,
    ...(projectId && { projectId }),
    ...(organizationId && { organizationId }),
    ...(timezone && { timezone }),
  };
}

/** Whether a stored config already holds the input (nothing to write). */
export function holdsServiceWorkerConfig(
  stored: Partial<ServiceWorkerConfigInput>,
  input: Required<Pick<ServiceWorkerConfigInput, 'projectId'>> & ServiceWorkerConfigInput
): boolean {
  return (
    stored.frontendVersion === input.frontendVersion &&
    stored.api === input.api &&
    stored.locale === input.locale &&
    stored.projectId === input.projectId &&
    stored.organizationId === input.organizationId &&
    stored.timezone === input.timezone
  );
}
